import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { BadgeCheck } from "lucide-react";
import { useState } from "react";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Can } from "@/components/can";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CompanyModal } from "@/features/companies/components/company-modal";
import { copyToClipboard } from "@/lib/clipboard";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

import type { ClaimTypeName } from "../lib/claim-types";
import { splitFlightIdentifier } from "../lib/flight";
import { GDS_CUSTOM_FIELD_LABELS, parseGdsMonitoringData } from "../lib/gds-monitoring-data";
import { BadgeClaimType } from "./badge-claim-type";

const airlineNameTriggerClass =
  "inline-block cursor-pointer font-semibold text-gray-700 underline decoration-indigo-500/30 decoration-2 underline-offset-2 hover:decoration-indigo-500/50";

interface FlightOverviewHeaderProps {
  claimId: string;
  flightId: string;
  marketingAirlineId: string;
  marketingAirlineName: string;
  marketingAirlineIcao?: string | null;
  marketingAirlineIconUrl?: string | null;
  marketingAirlineCompanyId?: string | null;
  operatingAirlineId?: string | null;
  operatingAirlineName?: string | null;
  operatingAirlineIconUrl?: string | null;
  operatingAirlineCompanyId?: string | null;
  gdsMonitoringData?: unknown;
  claimTypeName: ClaimTypeName | null;
  flightIdentifier: string;
  scheduledDepartureLocal?: Date;
  cancelledBy?: string | null;
  cancelledIndicator?: "RECORD" | "SEGMENT" | null;
  cancelledSignature?: string | null;
  cancelledDate?: Date | null;
}

/** Port of FlightOverviewHeader.vue. */
export function FlightOverviewHeader(props: FlightOverviewHeaderProps) {
  const queryClient = useQueryClient();
  const [companyModalAirlineId, setCompanyModalAirlineId] = useState<string | null>(null);

  const parsedGds = parseGdsMonitoringData(props.gdsMonitoringData);
  const hasGdsMonitoringData = parsedGds !== null;
  const customFields = parsedGds?.flightInfo?.customFields ?? {};
  const gdsCustomFields = GDS_CUSTOM_FIELD_LABELS.map(({ key, label }) => ({
    label,
    value: customFields[key],
  })).filter((row) => Boolean(row.value));

  const { flightNumber, iata } = splitFlightIdentifier(props.flightIdentifier);
  const icaoFlightIdentifier = props.marketingAirlineIcao
    ? props.marketingAirlineIcao + flightNumber
    : props.flightIdentifier;

  const displayIconUrl = props.operatingAirlineIconUrl || props.marketingAirlineIconUrl;

  // Air France flight status link for specific airlines
  const eligibleAirlines = ["AF", "KL", "TO", "A5"];
  const airFranceFlightStatusUrl =
    (iata && !eligibleAirlines.includes(iata)) || !props.scheduledDepartureLocal
      ? null
      : `https://wwws.airfrance.fr/en/flight-status?date=${format(new Date(props.scheduledDepartureLocal), "yyyyMMdd")}&flightNumber=${props.flightIdentifier}`;

  async function monitorFlight() {
    const { success } = await notify.promise(adminAPI.monitorFlight(props.flightId), {
      loading: "Synchronisation du vol…",
      success: "Vol synchronisé",
    });
    if (success) {
      await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(props.claimId) });
    }
  }

  return (
    <div className="bg-white px-2">
      <div className="flex py-2">
        <dt className="text-sm font-medium text-gray-500">
          {displayIconUrl ? (
            <div className="inline-flex h-14 w-14 flex-shrink-0">
              <img className="rounded-md" src={displayIconUrl} alt="Airline Logo" />
            </div>
          ) : null}
        </dt>
        <dd className="mt-1 block px-5 text-sm text-gray-900">
          {props.cancelledBy ? (
            <Tooltip>
              <TooltipTrigger
                render={
                  <span className="mr-2 inline-flex items-center gap-x-1.5 rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-900 ring-1 ring-red-200 ring-inset" />
                }
              >
                <svg className="h-1.5 w-1.5 fill-red-500" viewBox="0 0 6 6" aria-hidden="true">
                  <circle cx="3" cy="3" r="3" />
                </svg>
                Segment annulé
              </TooltipTrigger>
              <TooltipContent>
                {props.cancelledBy === "AIRLINE" ? "Annulé par la compagnie" : "Annulé par l'agence"}
              </TooltipContent>
            </Tooltip>
          ) : null}
          {props.claimTypeName ? (
            <BadgeClaimType className="my-1 mr-2" name={props.claimTypeName} emoji={false} />
          ) : null}

          <Can permissions={UPCLAIM_ROLES}>
            <span className="cursor-pointer text-xs font-normal text-gray-500">
              (
              <button type="button" onClick={() => copyToClipboard(props.flightId)}>
                ID du vol
              </button>
              <span className="px-2">|</span>
              <button type="button" onClick={monitorFlight}>
                sync
              </button>
              {airFranceFlightStatusUrl ? (
                <>
                  <span className="px-1 text-gray-400">|</span>
                  <a
                    href={airFranceFlightStatusUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-normal text-gray-500 hover:text-gray-900"
                  >
                    → Statut Air France
                  </a>
                </>
              ) : null}
              )
            </span>
          </Can>

          <div className="flex flex-wrap items-center gap-x-1 py-1">
            <span>Vol</span>
            <a
              href={`https://fr.flightaware.com/live/flight/${icaoFlightIdentifier}/history`}
              target="_blank"
              rel="noreferrer"
              className="link font-semibold"
            >
              {props.flightIdentifier}
            </a>
            {props.operatingAirlineId ? (
              <>
                <span>opéré par</span>
                <button
                  type="button"
                  className={airlineNameTriggerClass}
                  onClick={() => setCompanyModalAirlineId(props.operatingAirlineId as string)}
                >
                  {props.operatingAirlineName}
                </button>
              </>
            ) : (
              <>
                <span>commercialisé par</span>
                <button
                  type="button"
                  className={airlineNameTriggerClass}
                  onClick={() => setCompanyModalAirlineId(props.marketingAirlineId)}
                >
                  {props.marketingAirlineName}
                </button>
              </>
            )}

            {hasGdsMonitoringData ? (
              <Tooltip>
                <TooltipTrigger
                  render={<span className="inline-flex cursor-help items-center align-middle" />}
                >
                  <BadgeCheck className="h-5 w-5 flex-shrink-0 text-sky-500" />
                </TooltipTrigger>
                <TooltipContent className="max-w-xs bg-white p-0 text-left text-gray-900 shadow-lg ring-1 ring-gray-200">
                  <div className="w-full">
                    <div className="border-b border-gray-100 px-3 py-2">
                      <p className="text-xs font-semibold text-gray-900">Transporteur effectif</p>
                      <p className="text-[11px] text-gray-500">Source Amadeus</p>
                    </div>
                    {gdsCustomFields.length ? (
                      <dl className="space-y-1 px-3 py-2">
                        {gdsCustomFields.map((row) => (
                          <div key={row.label} className="flex justify-between gap-x-4">
                            <dt className="text-[11px] text-gray-500">{row.label}</dt>
                            <dd className="text-[11px] font-medium text-gray-900">{row.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <p className="px-3 py-2 text-[11px] text-gray-500">
                        Aucun détail de transporteur effectif.
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            ) : null}
          </div>
          {props.cancelledIndicator ? (
            <Can permissions={[...UPCLAIM_ROLES, "LAWYER"]}>
              <div className="flex items-center py-1">
                <pre className="rounded border border-gray-400 bg-gray-50 px-2 font-medium">
                  {props.cancelledIndicator === "SEGMENT" ? "HX" : "Cancel"}
                </pre>
                {props.cancelledSignature || props.cancelledBy ? (
                  <span className="mx-1">
                    par <span className="font-semibold">{props.cancelledSignature || props.cancelledBy}</span>
                  </span>
                ) : null}
                {props.cancelledDate ? (
                  <span>le {format(new Date(props.cancelledDate), "dd/MM/yyyy à dd'H'mm")} UTC</span>
                ) : null}
              </div>
            </Can>
          ) : null}
        </dd>
      </div>
      <CompanyModal
        open={companyModalAirlineId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCompanyModalAirlineId(null);
          }
        }}
        companyId={
          companyModalAirlineId === props.operatingAirlineId
            ? props.operatingAirlineCompanyId
            : props.marketingAirlineCompanyId
        }
        relation={companyModalAirlineId ? { airlineId: companyModalAirlineId } : undefined}
      />
    </div>
  );
}
